"use client";

import { useIsMobile } from "@ui/hooks/useBreakpoint";
import { useInView } from "@ui/lib/useInView";
import { cn } from "@ui/lib/utils";
import dynamic from "next/dynamic";
import { memo, useRef } from "react";
import { ErrorBoundary } from "react-error-boundary";

import type { Waveform3DComplexProps } from "./Waveform3DLazy";

const Waveform3D = dynamic(() => import("./Waveform3DLazy"), { ssr: false });
const WaveformSVG = dynamic(() => import("./waveformSVG"), { ssr: false });

export type WaveformRenderingStrategy = "3d" | "svg";

interface WaveformComplexProps extends Waveform3DComplexProps {
  className?: string;
  desktopStrategy?: WaveformRenderingStrategy;
  mobileStrategy?: WaveformRenderingStrategy;
}

const Waveform = memo(function Waveform({
  strategy,
  ...props
}: { strategy: WaveformRenderingStrategy } & Waveform3DComplexProps) {
  return strategy === "3d" ? <Waveform3D {...props} /> : <WaveformSVG {...props} />;
});

function WaveformComplex({ desktopStrategy = "3d", mobileStrategy = "3d", className, ...props }: WaveformComplexProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inView = useInView(wrapperRef, { once: true, threshold: 0.1 });
  const isMobile = useIsMobile();

  return (
    <>
      <ErrorBoundary fallback={null}>
        <div className={cn("h-400 relative w-full overflow-hidden", className)} ref={wrapperRef}>
          {!inView ? null : <Waveform strategy={isMobile ? mobileStrategy : desktopStrategy} {...props} />}
        </div>
      </ErrorBoundary>
    </>
  );
}

export default memo(WaveformComplex);
